import { PencilIcon } from "@heroicons/react/solid";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { Link } from "react-router-dom";
import { useOne } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import { CollapsibleSection } from "components/collapsibleSection";
import { IMKTBannerDetail } from "interfaces";
import { ClockIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { HistoryLodModal } from "components/historyLogModal";
import { isHaveActionPermission } from "utills/helpers";

export const BannerDetail: React.FC = () => {
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);

  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IMKTBannerDetail>({
    resource: "mkt/banners",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const _handleOpenHistoryLog = (e: any, id: string) => {
    e.stopPropagation();
    setOpenLogModalId(id);
  }

  const bannerData = data?.data;

  if (isLoading) {
    return <FullScreenLoading />
  }
  
  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Banner Detail"
        {...(isHaveActionPermission("banner", "mkt", "edit") && {
          additionalLabel: (
            <Link to={`/mkt/banner/edit`}>
              <Button>
                <span className="sr-only">Edit</span>
                <PencilIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Edit</span>
              </Button>
            </Link>
          ),
        })}
      />
      <CollapsibleSection 
        label="Sport events list"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.sport_list.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.sport_list.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner #{index + 1}
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Attraction events list"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.attraction_list.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.attraction_list.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner #{index + 1}
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Transport station"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.transport_station.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.transport_station.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Blogs home"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.blogs_home.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.blogs_home.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner #{index + 1}
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Blogs detail"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.blogs_detail.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.blogs_detail.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner #{index + 1}
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Workspace - Top banner"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.workspace.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.workspace.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner #{index + 1}
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Popup Homepage, Event list"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.popup_homepage.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.popup_homepage.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Popup “Payment status” page"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.popup_payment_status.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.popup_payment_status.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Homepage coupon"
        defaultOpen={false}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, bannerData?.homepage_coupon.id ?? "")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="box-container">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
            {(bannerData?.homepage_coupon.data ?? []).map((banner, index) => (
              <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                <div className="flex flex-col gap-y-4">
                  <div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                      Desktop Banner
                    </div>
                    <div className="overflow-hidden rounded-b-lg">
                      <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                    </div>
                  </div>
                  {banner.mobile_banner && (
                    <div>
                      <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                        Mobile Banner #{index + 1}
                      </div>
                      <div className="overflow-hidden rounded-b-lg">
                        <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>
      <HistoryLodModal
        resource={`mkt/banners/${openLogModalId}/history`}
        open={openLogModalId ? true : false}
        onClose={() => setOpenLogModalId(null)}
        key="mkt-banner"
        id="mkt-banner"
      />
      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}